import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  imageWithUploadSchema,
  legalEntitySchema,
  type BusinessType,
  type ImageWithUpload,
  type LegalEntity,
  type OrganizationProfile,
  type OrgProfileUpdate,
} from '@hero/schema';
import { useOrgProfileUpdateApi } from '@hero/api-hooks';
import { CheckIcon } from '@hero/web-icons';
import { Button } from '../../../ui/Button';
import { Spinner } from '../../../app/Spinner';
import { RegistrationNameField } from '../../../app/OrgProfileFields/RegistrationNameField';
import { RegistrationIdField } from '../../../app/OrgProfileFields/RegistrationIdField';
import { RegistrationImageField } from '../../../app/OrgProfileFields/RegistrationImageField';
import { TaxIdField } from '../../../app/OrgProfileFields/TaxIdField';
import { TaxImageField } from '../../../app/OrgProfileFields/TaxImageField';
import { BusinessTypeField } from '../../../app/OrgProfileFields/BusinessTypeField';
import { makeChipFor, type OrgProfileSectionContext } from './types';

/**
 * Legal-section resolver. Restricts the form to the fields the
 * carrier-dashboard "Company Legal" tab edits — registration
 * (name / id / image) and tax (id / image). License + address are
 * deliberately left out: those have their own dedicated UI flows
 * and don't belong on the financing gate either.
 *
 * Re-using `legalEntitySchema.pick({...}).partial()` keeps the
 * validation in sync with the backend's
 * `legalEntitySchema.partial()` body so client + server agree on
 * "what counts as a non-empty legal field".
 */
const legalSchema = legalEntitySchema
  .pick({
    registrationName: true,
    registrationId: true,
    registrationImage: true,
    taxId: true,
    taxImage: true,
    businessType: true,
  })
  .partial()
  .extend({
    // `legalEntitySchema` types `registrationImage` / `taxImage` as
    // the persisted shape — relaxing to the `imageWithUpload` union
    // here lets us hold fresh `{ upload }` payloads in form state
    // while still accepting round-tripped `{ id, url }` values.
    registrationImage: imageWithUploadSchema.optional(),
    taxImage: imageWithUploadSchema.optional(),
  });

type LegalFormValues = z.infer<typeof legalSchema>;

export interface CarrierLegalFormProps {
  profile: Partial<OrganizationProfile> | undefined;
  /**
   * Drives the financing-chip variants:
   * - `'profile'` (default) renders no chips — every field is
   *   genuinely optional on the dashboard profile tab.
   * - `'financing'` decorates the four hard fields (legal name,
   *   CR number, tax id, CR document, tax document) as `required`.
   */
  displayFor?: OrgProfileSectionContext;
  hideSubmit?: boolean;
  disabled?: boolean;
  onSaved?: (patch: { legal: NonNullable<OrgProfileUpdate['legal']> }) => void;
}

/**
 * `CarrierLegalForm` — Company Legal tab. Edits the legal entity
 * fields used by lenders to verify the carrier (registration
 * name, CR number + scan, tax id + scan).
 *
 * Behaviour:
 *  - Builds a section-only patch: only dirty fields end up in the
 *    `legal: { … }` object, so the user can change just one field
 *    and the others stay untouched on the server (the new
 *    `OrgProfileService` only writes the present keys).
 *  - In the `displayFor='financing'` context, surfaces the
 *    financing chip on the hard fields. The chip is purely
 *    informational here — the form itself doesn't enforce
 *    required-ness (the financing apply gate does that via its
 *    own readiness check).
 */
export function CarrierLegalForm({
  profile,
  displayFor = 'profile',
  hideSubmit,
  disabled,
  onSaved,
}: CarrierLegalFormProps) {
  const { t } = useTranslation('m-organization');
  // The CR written here is also what lets an Org issue government waybills on
  // Hero's Bayan licence, so it invalidates the `app` entity as well as `org`.
  const update = useOrgProfileUpdateApi({ invalidateQueryKeys: [['app']] });
  const chip = makeChipFor(displayFor);

  const defaultValues = useMemo<LegalFormValues>(
    () => ({
      registrationName: profile?.registrationName,
      registrationId: profile?.registrationId,
      registrationImage: profile?.registrationImage as ImageWithUpload | undefined,
      taxId: profile?.taxId,
      taxImage: profile?.taxImage as ImageWithUpload | undefined,
      businessType: profile?.businessType as BusinessType | undefined,
    }),
    [
      profile?._id,
      profile?.registrationName,
      profile?.registrationId,
      profile?.taxId,
      profile?.businessType,
    ],
  );

  const methods = useForm<LegalFormValues>({
    resolver: zodResolver(legalSchema),
    defaultValues,
    mode: 'onSubmit',
  });

  useEffect(() => {
    methods.reset(defaultValues);
  }, [defaultValues, methods]);

  const onSubmit = methods.handleSubmit(async values => {
    const dirty = methods.formState.dirtyFields;
    const legal: NonNullable<OrgProfileUpdate['legal']> = {};
    // Building the dirty-only patch by hand — `Object.entries` +
    // a loop is shorter but loses TS narrowing on the section
    // shape, which is critical because the backend's
    // `bankCommonInfoSchema.partial()` and
    // `legalEntitySchema.partial()` only accept their own keys.
    if (dirty.registrationName) legal.registrationName = values.registrationName;
    if (dirty.registrationId) legal.registrationId = values.registrationId;
    if (dirty.registrationImage)
      legal.registrationImage = values.registrationImage as LegalEntity['registrationImage'];
    if (dirty.taxId) legal.taxId = values.taxId;
    if (dirty.taxImage) legal.taxImage = values.taxImage as LegalEntity['taxImage'];
    if (dirty.businessType) legal.businessType = values.businessType;
    if (Object.keys(legal).length === 0) return;
    try {
      await update.mutateAsync({ legal });
      onSaved?.({ legal });
    } catch (err) {
      console.error('Failed to save carrier legal:', err);
    }
  });

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit} className="space-y-6" noValidate>
        <RegistrationNameField
          name="registrationName"
          disabled={disabled}
          financing={chip('hard')}
        />
        <BusinessTypeField name="businessType" disabled={disabled} />
        {/* Bayan sends this as `carrier.moi` for a company carrier, so the
            Logisti chip lives here rather than on a field of its own. */}
        <RegistrationIdField
          name="registrationId"
          disabled={disabled}
          financing={chip('hard')}
          logisti="required"
        />
        <RegistrationImageField
          name="registrationImage"
          disabled={disabled}
          financing={chip('hard')}
        />
        <TaxIdField name="taxId" disabled={disabled} financing={chip('hard')} />
        <TaxImageField name="taxImage" disabled={disabled} financing={chip('hard')} />

        {!hideSubmit ? (
          <div className="flex items-center justify-end">
            <Button type="submit" disabled={disabled || update.isPending}>
              {update.isPending ? (
                <Spinner />
              ) : (
                <span className="flex items-center gap-2">
                  <CheckIcon className="size-4" />
                  {t('buttons.save')}
                </span>
              )}
            </Button>
          </div>
        ) : null}
      </form>
    </FormProvider>
  );
}
